/* eslint-disable local/ensure-wac-token-usage */
import { ReactNode, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { ButtonColor } from '@/components/wpl/button/src/button';
import { ImageProps } from '@/components/wpl/image/src/image';
import { ProductNavBarProps } from '@/components/wpl/product-navbar/src/product-navbar';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { DataEventProps, ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { Layout } from '@/libraries/wpl/foundations/src/grid';
import { LogoBrand } from '@/libraries/wpl/foundations/src/logo';
import type { EmbeddedSignupModuleProps, PaidHeroProps } from '@/patterns/paid-hero/src/paid-hero';
import { PaidSocialProofProps } from '@/patterns/paid-landings/paid-social-proof/src/paid-social-proof';
import { PaidTabsProps } from '@/patterns/paid-landings/paid-tabs/src/paid-tabs';
import { PatternThreeAlternatingImageTextProps } from '@/patterns/three-alternating-image-text/src/three-alternating-image-text';
import { PatternCTAMobileProps } from '@/patterns/wpl/cta-primary/src/cta-primary';
import { useFeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { JourneyLocalStoragePersona, useAnchorLocalStorage } from '@/utils/local-storage-hook.tsx';

import { getLocalizedImage } from '../../../localization/localizable-images/paid-lp-conf/localized-images-conf-features';
import { useLoggedInUser } from '../../../utils/logged-in-user-provider';

import { PaidLandingProps, PaidLandingTemplate, PaidLandingTypes } from './paid-landing.template';

interface TabsProps extends PaidTabsProps {
	styles: object;
}

export interface PaidLandingConfProps {
	landingType?: PaidLandingTypes;
	productNavProps?: ProductNavBarProps;
	heroProps: PaidHeroProps;
	tabsProps: TabsProps;
	alternatingTextAndImageProps?: PatternThreeAlternatingImageTextProps;
	ctaProps?: Partial<PatternCTAMobileProps> & { layout?: Layout };
	testimonialProps?: PaidSocialProofProps;
	customHeroSection?: ReactNode;
	keyword?: string;
	subAnchor?: string;
}

export const PaidLandingConfTemplate = (props: PaidLandingConfProps) => {
	const intl = useIntl();
	const userInfo = useLoggedInUser();
	const { setAnchorLocalStorage } = useAnchorLocalStorage();
	useEffect(() => {
		const { keyword, subAnchor } = props;
		const anchorLocalStorage: JourneyLocalStoragePersona = {};
		if (!!keyword) {
			anchorLocalStorage.keyword = keyword;
		}
		if (!!subAnchor) {
			anchorLocalStorage.subAnchor = subAnchor;
		}
		if (Object.keys(anchorLocalStorage).length > 0) {
			setAnchorLocalStorage(anchorLocalStorage);
		}
	}, [props.keyword]);
	const embeddedSignup: EmbeddedSignupModuleProps | undefined =
		!userInfo.isUserLoggedIn && !userInfo.requestInProgress
			? {
					bundle: 'confluence',
					edition: 'free',
					useCurrentRedirect: true,
				}
			: undefined;
	useFeatureExposed('cfind-1254-paid-embedded-signup-form', 'Test', {
		conditions: [!!embeddedSignup],
	});

	const finalProps = {
		landingType: 'confluence-paid',
		productNavProps: {
			productHref: useLocalizedHref('/software/confluence'),
			productName: 'confluence' as LogoBrand,
			ariaLabel: intl.formatMessage({
				id: 'wacConfluenceNavigation.logo-label',
				defaultMessage: 'Confluence logo',
			}),
			dataProductKey: 'confluence' as ProductKeyTypes,
			showFreeHrefButton: false,
		},
		alternatingTextAndImageProps: {
			backgroundColor: '#e6fcff',
			horizontalRuleColor: '#dfe1e5',
			//Row 1
			rowOneHeader: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowOneHeader',
				defaultMessage: 'Move work forward',
			}),
			rowOneBody: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowOneBody',
				defaultMessage:
					'Kickstart collaboration with a flexible workspace — from actionable meeting notes to inspiring project plans.',
			}),
			rowOneImageLink: getLocalizedImage('row one', intl.locale),
			rowOneImageAltText: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowOneImageAltText',
				defaultMessage: 'Screenshot of IT template',
			}),
			//Row 2
			rowTwoHeader: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowTwoHeader',
				defaultMessage: 'Capture all the knowledge',
			}),
			rowTwoBody: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowTwoBody',
				defaultMessage:
					"Save everyone time by harnessing your teams' collective knowledge into easy-to-find information.",
			}),
			rowTwoImageLink: getLocalizedImage('row two', intl.locale),
			rowTwoImageAltText: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowTwoImageAltText',
				defaultMessage: 'Screenshot of strategic template in use',
			}),
			//Row 3
			rowThreeHeader: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowThreeHeader',
				defaultMessage: 'Inspire conversation & connection',
			}),
			rowThreeBody: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowThreeBody',
				defaultMessage:
					'Motivate teams company-wide to share announcements, comment, and provide feedback.',
			}),
			rowThreeImageLink: getLocalizedImage('row three', intl.locale),
			rowThreeImageAltText: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.alternatingTextAndImage.rowThreeImageAltText',
				defaultMessage: 'Screenshot of IT template',
			}),
		},
		ctaProps: {
			headerText: intl.formatMessage({
				id: 'ConfluenceSoftwarePage.paidLpConfDefault.ctaSection.title',
				defaultMessage:
					'Make quick decisions, gain alignment, and transform how your team works together.',
			}),
			ctaProps: {
				buttonProps: {
					color: 'yellow' as ButtonColor,
					url: '#start-trial',
					text: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.ctaSection.button',
						defaultMessage: 'Get it free',
					}),
					dataEventProps: {
						'data-event': 'clicked',
						'data-event-container': 'ctaSection',
						'data-event-component': 'linkButton',
						'data-product-key': 'confluence',
					} as DataEventProps,
				},
			},
		},
		testimonialProps: {
			headerText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.paidLpConfDefault.customerSection.customersUseConfluenceToMoveWorkForward',
				defaultMessage: '75,000+ customers use Confluence to move work forward.',
			}),
			logoSize: 'large',
			logos: [
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:b12e4075-84f4-467e-9acf-f062c8fdaece/twilio-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.twilio',
						defaultMessage: 'Twilio logo',
					}),
					width: '107px',
					height: '32px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:35c6a74c-a259-4dac-8df8-ea8c5b12f44d/nasa-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.nasa',
						defaultMessage: 'NASA logo',
					}),
					width: '71px',
					height: '60px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:5a1e47e3-be98-4279-a3a2-c626164dbd5d/illumina-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.illumina',
						defaultMessage: 'Illumina logo',
					}),
					width: '100px',
					height: '24px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:731482d9-54c4-48e7-a10b-ab601ca4fbd6/docker-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.docker',
						defaultMessage: 'Docker logo',
					}),
					width: '126px',
					height: '33px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:7343b372-8a2a-4156-8755-bcf38735ca6b/morningstar-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.morningStart',
						defaultMessage: 'MorningStart logo',
					}),
					width: '134px',
					height: '30px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:c67f93d6-d8be-4970-b4f5-addf24c13e4e/rtr-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.rtr',
						defaultMessage: 'RTR logo',
					}),
					width: '43.71px',
					height: '60px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:b62031f5-0770-46cb-a1c2-50cdacb8a0b0/anz-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.anz',
						defaultMessage: 'ANZ logo',
					}),
					width: '102px',
					height: '32px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:475f41d8-45f4-48c9-a9b2-d78215ae4b16/nyt-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.theNewYorkTimes',
						defaultMessage: 'The New York Times logo',
					}),
					width: '223px',
					height: '32px',
					loading: 'lazy',
				},
				{
					src: 'https://wac-cdn.atlassian.com/dam/jcr:6841df63-0c84-4075-9ac5-1ed0f8ddea17/gopro-n50.svg',
					alt: intl.formatMessage({
						id: 'ConfluenceSoftwarePage.paidLpConfDefault.customerSection.imgAltText.goPro',
						defaultMessage: 'GoPro logo',
					}),
					width: '110px',
					height: '35px',
					loading: 'lazy',
				},
			] as ImageProps[],
		},
		...props,
		heroProps: {
			...props.heroProps,
			embeddedSignup,
		},
	} as PaidLandingProps;

	return <PaidLandingTemplate {...finalProps} />;
};
